import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ColDef } from 'ag-grid-enterprise';
import 'ag-grid-enterprise';
import {
  ColumnResizedEvent,
  GridApi,
  GridReadyEvent,
  IsRowSelectable,
  Module,
  RowClickedEvent,
  RowNode,
  RowGroupingDisplayType,
  FirstDataRenderedEvent,
} from 'ag-grid-community';
import { CustomTooltipComponent } from './custom-tooltip/custom-tooltip.component';
import { Subscription } from 'rxjs';
import { CommunicationService } from '../../services/communication.service';
import { AppConst } from '@app/App.const';
import { CustomDateComponent } from '../custom-date/custom-date.component';
import { getPaginationSize, setPaginationSize } from '../../utils/ag-grid';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  standalone: false 
})
export class TableComponent implements OnInit, OnDestroy {
  public tooltipShowDelay: any;
  public frameworkComponents: any;
  public currentPage = 3;
  public maxPages = 5;
  @Input() showTable = true;
  @Input() columnDefs: ColDef[] = [];
  @Input() rowData: any[] = [];
  @Input() rowClassRules: any = {};
  @Input() defaultColDef: ColDef = {
    flex: 1,
    minWidth: 100,
    resizable: true,
    autoHeight: true,
    sortable: true,
    floatingFilter: true,
  };
  public showMenu: boolean = true;
  @Input() suppressRowClickSelection = true;
  @Input() enableRangeSelection=false;
  @Input() groupRowRendererParams = {};
  @Input() rowSelection:"single" | "multiple" = "multiple"; ;
  @Input() suppressAggFuncInHeader = true;
  @Input() suppressCellSelection = false;
  @Input() paginationRequired = true;
  @Input() masterDetail = false;
  @Input() detailCellRendererParams = {};
  @Input() paginationPageSize=AppConst.paginationPageSize;
  @Input() isRowMaster = () => {
    return false;
  };
  @Input() defaultPage = 0;
  @Input() pageChangeHandler = (page:any) => {};
  @Input() overlayNoRowsTemplate = 'No Data Found';
  @Input() pageSizeSelection=false
  @Input() paginationPageSizeSelector: number[] | boolean = [10,20,50,100];
  @Input() tableViewFullSizeEnabled=false



  @Output() cellClicked = new EventEmitter<any>();
  @Output() gridReady = new EventEmitter<any>();
  @Output() rowClicked = new EventEmitter<any>();
  @Output() columnResized = new EventEmitter<any>();
  @Output() selectionChanged = new EventEmitter<any>();
  @Output() cellEditingStopped = new EventEmitter<any>();
  @Output() cellValueChanged=new EventEmitter<any>();

  public overlayLoadingTemplate = `<span class="ag-overlay-loading-center">Loading...</span>`;
  public rowGroupPanelShow: any = 'always';
  public groupDisplayType: RowGroupingDisplayType = 'groupRows';
  public modules: Module[] = [];
  public gridColumnApi: any;
  public gridApi!: GridApi;
  private pendingFilterModel: any;
  private hasPendingFilterModel = false;
  private pendingSortModel: any[] = [];
  private hasPendingSortModel = false;
  public searchSubscribtion = new Subscription();
  public filterSubscription = new Subscription();
  public sortSubscription = new Subscription();
  @Input() isRowSelectable: IsRowSelectable<any> | undefined = (
    params: any
  ) => {
    return !!params.data && !params.data.isError;
  };

  constructor(public communicationService: CommunicationService) {
    this.tooltipShowDelay = 0;
    this.frameworkComponents = { customTooltip: CustomTooltipComponent ,agDateInput: CustomDateComponent};
  }
  ngOnInit(): void {
    const quickFilterParser = (quickFilter: string): string[] => [quickFilter];
    const quickFilterMatcher = (
      quickFilterParts: string[],
      rowQuickFilterAggregateText: string
    ): boolean => {
      const filterText = quickFilterParts[0]?.toLowerCase() || '';
      return rowQuickFilterAggregateText.toLowerCase().includes(filterText);
    };
    this.searchSubscribtion =
      this.communicationService.isTableOperationsEventCalled.subscribe(
        (res: { event: string; data: any }) => {
          switch (res.event) {
            case 'downloadExcel':
              if (this.gridApi) {
                this.exportToExcel(res.data);
              }
              break;
            case 'search':
              if (!this.gridApi) {
                break;
              }
              this.gridApi.setGridOption('quickFilterParser', quickFilterParser);
              this.gridApi.setGridOption('quickFilterMatcher', quickFilterMatcher);
              this.gridApi.setGridOption('quickFilterText', res.data);
              break;
            case 'showLoader':
              this.gridApi?.setGridOption("loading", true);
              break;
            case 'hideLoader':
              this.gridApi?.hideOverlay && this.gridApi?.hideOverlay();
              break;
            case 'showNoRows':
              this.gridApi?.setGridOption("loading", false);
              this.gridApi?.hideOverlay && this.gridApi?.hideOverlay();
              break;
          }
        }
      );
    this.filterSubscription=this.communicationService.filterChangedEvent.subscribe((res) => {
      this.pendingFilterModel = res;
      this.hasPendingFilterModel = true;
      this.applyPendingFilterModel();
    }); 
    this.sortSubscription=this.communicationService.sortChangedEvent.subscribe((res) => {
      this.pendingSortModel = res || [];
      this.hasPendingSortModel = true;
      this.applyPendingSortModel();
    });
  }
  //in onGridReady, store the api for later use
  public onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridColumnApi = params.api;
    this.applyPendingFilterModel();
    this.applyPendingSortModel();
    this.gridReady.emit(params);
  }

  private applyPendingFilterModel(): void {
    if (!this.hasPendingFilterModel || !this.gridApi?.setFilterModel) {
      return;
    }

    this.gridApi.setFilterModel(this.pendingFilterModel);
    this.pendingFilterModel = undefined;
    this.hasPendingFilterModel = false;
  }

  private applyPendingSortModel(): void {
    if (!this.hasPendingSortModel || !this.gridApi?.applyColumnState) {
      return;
    }

    this.gridApi.applyColumnState({
      state: this.pendingSortModel,
      defaultState: { sort: null },
    });
    this.pendingSortModel = [];
    this.hasPendingSortModel = false;
  }
  // This allow download of unfiltered raw data to excel, Currently configured for Usermanagement, please make changes if required anywhere else
  public exportToExcel(title: string) {
    this.gridApi.exportDataAsExcel( 
      {
        exportedRows: 'all',
        fileName: title,
        processCellCallback: params => {
          const colId = params.column.getColId();
          const teams = params.value;

          if (colId === 'teams' && Array.isArray(teams)) {
            return teams.map(team => team.teamName).join(', ');
          }

          return params.value;
        },

      }
    );
  }

  filterChanged(event: any) {
    this.communicationService.onFilterChange(this.gridApi.getFilterModel() || {});
  }

  public sortChanged(event: any) {
    this.communicationService.onSortChange(
      this.gridApi
        .getColumnState()
        .filter((column: any) => column.sort)
        .map((column: any) => ({
          colId: column.colId,
          sort: column.sort,
          sortIndex: column.sortIndex,
        }))
    );
  }

  public onRowClicked(params: RowClickedEvent) {
    this.rowClicked.emit(params);
  }

  public onColumnResized(params: ColumnResizedEvent) {
    this.columnResized.emit(params);
  }

  public onSelectionChanged(params: any) {
    this.selectionChanged.emit(params);
  }

  ngOnDestroy() {
    this.searchSubscribtion.unsubscribe();
    this.filterSubscription.unsubscribe();
    this.sortSubscription.unsubscribe();
  }

  public editCell(event: any) {
    //for future reference
  }
  onFirstDataRendered(params: FirstDataRenderedEvent) {
    params.api.paginationGoToPage(this.defaultPage);
  }
  onPaginationChanged(event: any) {
    if(event.newPage){
      this.pageChangeHandler(event.api.paginationGetCurrentPage() || 0);
    }
  }

  public onCellEditingStopped(event: any) {
    this.cellEditingStopped.emit(event);
  }

  public onCellValueChanged(event: any) {
    this.cellValueChanged.emit(event);
  }

  public onCellClicked(event: any) {
    this.cellClicked.emit(event);
  }

  public onRowGroupOpened(params: any) {
    if (params?.node?.expanded) {
      this.gridApi.forEachNode(function (node) {
        if (
          node.expanded &&
          node.id !== params.node.id &&
          node.uiLevel === params.node.uiLevel
        ) {
          node.setExpanded(false);
        }
      });
    }
  }
}
